import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { getState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, of, switchMap } from 'rxjs';
import {
  AccessListDetails,
  AccessListsDetailsResponse,
  AccessListsService,
  CreateCustomAccessListRequest,
  CustomAccessList,
  UpdateAccessListConfigurationRequest,
  UpdateCustomAccessListRequest,
} from '@bn/app-shield';
import { StringTMap } from '@bn/shared/entities/index';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/shared/state-store';

// Define events for store actions
const events = getStoreEvents('SHIELD_ACCESS_LIST', [
  'ACCESS_LISTS',
  'ACCESS_LIST_DETAILS',
  'ACCESS_LIST_DETAILS_ADD',
  'ACCESS_LIST_DETAILS_UPDATE',
  'ACCESS_LIST_DETAILS_DELETE',
  'ACCESS_LIST_ENUMS',
  'ACCESS_LISTS_MODAL',
]);

export interface ShieldAccessListsStoreType extends AccessListsDetailsResponse {
  shieldZoneId: number | null;
  customAccessListDetails: CustomAccessList | null;
  enums: StringTMap<StringTMap<string>> | null;
}

const initialState: ShieldAccessListsStoreType = {
  shieldZoneId: null,
  managedLists: null,
  customLists: null,
  customEntryCount: null,
  customEntryLimit: null,
  customListCount: null,
  customListLimit: null,
  customAccessListDetails: null,
  enums: null,
};
export const ShieldAccessListsApiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield.AccessLists'),
  withState<ShieldAccessListsStoreType>(initialState),
  withDefaultRequestStatusState(),
  withMethods((store) => ({
    clearModalStates: () => updateState(store, events.ACCESS_LISTS_MODAL.CLEAR, store.setDefault([StoreRequest.Item, StoreRequest.Update, StoreRequest.Add])),
  })),

  withMethods((store, accessListsService = inject(AccessListsService), destroyRef = inject(DestroyRef)) => ({
    getEnums: rxMethod<{ shieldZoneId: string }>(
      exhaustMap(({ shieldZoneId }) => {
        updateState(store, events.ACCESS_LIST_ENUMS.REQUEST, store.setLoading(StoreRequest.Item, ['Enums']));

        return accessListsService.getAccessListEnums({ shieldZoneId }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (data) => {
              updateState(
                store,
                events.ACCESS_LIST_ENUMS.SUCCESS,
                {
                  enums: data,
                },
                store.setFinished(StoreRequest.Item, ['Enums']),
              );
            },
            error: ({ error }) => updateState(store, events.ACCESS_LIST_ENUMS.ERROR, store.setError(StoreRequest.Item, error, ['Enums'])),
          }),
        );
      }),
    ),
  })),

  withMethods((store, accessListsService = inject(AccessListsService), destroyRef = inject(DestroyRef)) => ({
    getAccessLists: rxMethod<{ shieldZoneId: number; cleanRequest?: boolean }>(
      exhaustMap(({ shieldZoneId, cleanRequest }) => {
        store.getEnums({ shieldZoneId: shieldZoneId.toString() });

        if (store.shieldZoneId() === shieldZoneId && !cleanRequest) {
          return of(null);
        }

        updateState(store, events.ACCESS_LISTS.REQUEST, store.setLoading(StoreRequest.List));

        return accessListsService.getAccessLists({ shieldZoneId }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, ...others } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.ACCESS_LISTS.ERROR,
                  store.setErrorMessage(StoreRequest.List, error?.message ?? 'There was an error while trying to get Access List data.'),
                );
              }

              updateState(
                store,
                events.ACCESS_LISTS.SUCCESS,
                {
                  ...others,
                  shieldZoneId,
                },
                store.setFinished(StoreRequest.List),
              );
            },
            error: ({ error }) => updateState(store, events.ACCESS_LISTS.ERROR, store.setError(StoreRequest.List, error)),
          }),
        );
      }),
    ),
  })),
  withMethods((store, accessListsService = inject(AccessListsService), destroyRef = inject(DestroyRef)) => ({
    updateAccessList: rxMethod<{
      id: number;
      shieldZoneId: number;
      listKey: 'managedLists' | 'customLists';
      data: UpdateAccessListConfigurationRequest;
    }>(
      switchMap(({ id, shieldZoneId, listKey, data }) => {
        updateState(store, events.ACCESS_LIST_DETAILS_UPDATE.REQUEST, store.setLoading(StoreRequest.Update));

        return accessListsService.updateAccessListConfiguration({ id, shieldZoneId, updateAccessListConfigurationRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.ACCESS_LIST_DETAILS_UPDATE.ERROR,
                  store.setErrorMessage(StoreRequest.Update, error?.message ?? 'There was an error while trying to get Access List data.'),
                );
              }

              const currentList = getState(store)?.[listKey] as AccessListDetails[] | null;
              const updatedList = currentList?.map((item) => (item.configurationId === id ? { ...item, ...data } : item));

              updateState(store, events.ACCESS_LIST_DETAILS_UPDATE.SUCCESS, { [listKey]: updatedList }, store.setFinished(StoreRequest.Update));
            },
            error: ({ error }) => updateState(store, events.ACCESS_LIST_DETAILS_UPDATE.ERROR, store.setError(StoreRequest.Update, error)),
          }),
        );
      }),
    ),

    addAccessList: rxMethod<{
      shieldZoneId: number;
      data: CreateCustomAccessListRequest;
      customFields?: string[];
    }>(
      switchMap(({ customFields = [], shieldZoneId, data }) => {
        updateState(store, events.ACCESS_LIST_DETAILS_ADD.REQUEST, store.setLoading(StoreRequest.Add, customFields));

        return accessListsService.createCustomAccessList({ shieldZoneId, createCustomAccessListRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data } = response;

              if ((error && !error?.success) || !data) {
                updateState(store, events.ACCESS_LIST_DETAILS_ADD.ERROR, store.setErrorMessage(StoreRequest.Add, error?.message ?? '', customFields));

                return;
              }

              updateState(store, events.ACCESS_LIST_DETAILS_ADD.SUCCESS, store.setFinished(StoreRequest.Add, customFields));

              // Note: we request list again because models are not the same add/list
              store.getAccessLists({
                shieldZoneId,
                cleanRequest: true,
              });
            },
            error: ({ error }) =>
              updateState(store, events.ACCESS_LIST_DETAILS_ADD.ERROR, store.setErrorMessage(StoreRequest.Add, error?.error?.message ?? '', customFields)),
          }),
        );
      }),
    ),

    getAccessListsDetails: rxMethod<{
      id: number;
      shieldZoneId: number;
    }>(
      exhaustMap(({ id, shieldZoneId }) => {
        updateState(store, events.ACCESS_LIST_DETAILS.REQUEST, store.setLoading(StoreRequest.Item));

        return accessListsService.getCustomAccessList({ id, shieldZoneId }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.ACCESS_LIST_DETAILS.ERROR,
                  store.setErrorMessage(StoreRequest.Item, error?.message ?? 'There was an error while trying to get Access List details.'),
                );
              }

              updateState(
                store,
                events.ACCESS_LIST_DETAILS.SUCCESS,
                {
                  customAccessListDetails: data,
                },
                store.setFinished(StoreRequest.Item),
              );
            },
            error: ({ error }) => updateState(store, events.ACCESS_LIST_DETAILS.ERROR, store.setError(StoreRequest.Item, error)),
          }),
        );
      }),
    ),

    updateAccessListDetails: rxMethod<{
      id: number;
      shieldZoneId: number;
      data: UpdateCustomAccessListRequest;
      customFields?: string[];
    }>(
      switchMap(({ customFields = [], id, shieldZoneId, data }) => {
        updateState(store, events.ACCESS_LIST_DETAILS_UPDATE.REQUEST, store.setLoading(StoreRequest.Update, customFields));

        return accessListsService.updateCustomAccessList({ id, shieldZoneId, updateCustomAccessListRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error } = response;

              if (error && !error?.success) {
                updateState(store, events.ACCESS_LIST_DETAILS_UPDATE.ERROR, store.setErrorMessage(StoreRequest.Update, error?.message ?? '', customFields));
              }

              updateState(store, events.ACCESS_LIST_DETAILS_UPDATE.SUCCESS, store.setFinished(StoreRequest.Update, customFields));

              // Note: we request list again because models are not the same add/list
              store.getAccessLists({
                shieldZoneId,
                cleanRequest: true,
              });
            },
            error: ({ error }) =>
              updateState(
                store,
                events.ACCESS_LIST_DETAILS_UPDATE.ERROR,
                store.setErrorMessage(StoreRequest.Update, error?.error?.message ?? '', customFields),
              ),
          }),
        );
      }),
    ),

    deleteAccessList: rxMethod<{
      id: number;
      shieldZoneId: number;
    }>(
      exhaustMap(({ id, shieldZoneId }) => {
        updateState(store, events.ACCESS_LIST_DETAILS_DELETE.REQUEST, store.setLoading(StoreRequest.Delete));

        return accessListsService.deleteCustomAccessList({ id, shieldZoneId }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { success, errorKey } = response;

              if (errorKey && !success) {
                updateState(
                  store,
                  events.ACCESS_LIST_DETAILS_DELETE.ERROR,
                  store.setErrorMessage(StoreRequest.Delete, 'There was an error while trying to delete Access List.'),
                );
              }

              const currentList = getState(store)?.customLists as AccessListDetails[] | null;
              const customLists = currentList?.filter((item) => item.listId !== id);

              updateState(store, events.ACCESS_LIST_DETAILS_DELETE.SUCCESS, { customLists }, store.setFinished(StoreRequest.Delete));

              // Note: we request list again because we need limits for create list button updated
              store.getAccessLists({
                shieldZoneId,
                cleanRequest: true,
              });
            },
            error: ({ error }) => updateState(store, events.ACCESS_LIST_DETAILS_DELETE.ERROR, store.setError(StoreRequest.Delete, error)),
          }),
        );
      }),
    ),
  })),
);
